import { LEAD_PRICE, PLAN_INCLUDED_LEADS, PLAN_PRICES, money, Lang } from '@/data/constants';

/**
 * "HOW GIGGONE WORKS" — ONE SOURCE OF TRUTH.
 *
 * Every surface that explains the buying flow (the public tutorial player, the
 * visual workflow, the /how-it-works page, the dashboard "Watch Demo" modal,
 * the post-purchase walkthrough and the onboarding email) reads from this file,
 * so the explanation a customer sees before buying is word-for-word the same
 * one they see after buying. Prices and credit counts come from constants.ts —
 * never retyped here.
 */

export type TutorialAudience = 'sub' | 'gc';

export type TutorialStepId =
  | 'account'
  | 'verify'
  | 'browse'
  | 'buy'
  | 'credits'
  | 'unlock'
  | 'info'
  | 'contact'
  | 'win';

type Bi = { en: string; es: string };

export interface TutorialStep {
  id: TutorialStepId;
  /** Short label used in the workflow diagram. */
  label: Bi;
  title: Bi;
  /** Narration read aloud / shown as captions. */
  narration: Bi;
  /** Optional audience-specific override of the narration. */
  narrationGc?: Bi;
  /** "Where do I find this?" pointer inside the product. */
  where: Bi;
  /** True when the step belongs to the visual workflow strip. */
  workflow: boolean;
}

const lead = money(LEAD_PRICE);
const basic = `${money(PLAN_PRICES.basic)}/mo · ${PLAN_INCLUDED_LEADS.basic}`;
const pro = `${money(PLAN_PRICES.pro)}/mo · ${PLAN_INCLUDED_LEADS.pro}`;
const premium = `${money(PLAN_PRICES.premium)}/mo · ${PLAN_INCLUDED_LEADS.premium}`;

export const TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: 'account',
    workflow: true,
    label: { en: 'Create Account', es: 'Crear cuenta' },
    title: { en: 'Create your free account', es: 'Cree su cuenta gratis' },
    narration: {
      en: 'Tap Sign Up and choose Subcontractor. Add your name, company, phone, trades and service area. Creating an account is free and no license is required to register.',
      es: 'Toque Registrarse y elija Subcontratista. Agregue su nombre, empresa, teléfono, oficios y zona de servicio. Crear una cuenta es gratis y no se requiere licencia para registrarse.',
    },
    narrationGc: {
      en: 'Tap Sign Up and choose General Contractor. Add your company, phone, trades and service area. Your account can both post work orders for free and buy leads to win jobs for your own crew.',
      es: 'Toque Registrarse y elija Contratista General. Agregue su empresa, teléfono, oficios y zona de servicio. Su cuenta puede publicar órdenes de trabajo gratis y también comprar leads para ganar trabajos.',
    },
    where: { en: 'Sign Up button — top right of every page', es: 'Botón Registrarse — arriba a la derecha' },
  },
  {
    id: 'verify',
    workflow: true,
    label: { en: 'Verify Email', es: 'Verificar correo' },
    title: { en: 'Verify your email', es: 'Verifique su correo' },
    narration: {
      en: 'We send a one-time confirmation link to your inbox. Open it within 24 hours. A verified email is required before you can buy or unlock leads, and it is where your receipts are sent. Not in your inbox? Check spam, or resend it from Profile Settings.',
      es: 'Enviamos un enlace de confirmación de un solo uso a su correo. Ábralo dentro de 24 horas. Un correo verificado es obligatorio antes de comprar o desbloquear leads, y ahí llegan sus recibos. ¿No lo ve? Revise spam o reenvíelo desde Configuración de Perfil.',
    },
    where: { en: 'Your inbox · Profile Settings → Resend link', es: 'Su correo · Perfil → Reenviar enlace' },
  },
  {
    id: 'browse',
    workflow: true,
    label: { en: 'Browse Leads', es: 'Ver leads' },
    title: { en: 'Browse real project leads', es: 'Explore proyectos reales' },
    narration: {
      en: 'Open Projects to see every open job. Filter by trade, state, city, budget and residential or commercial. Each card shows the scope, city and budget for free. The homeowner name, phone, email and exact address stay hidden until you unlock.',
      es: 'Abra Proyectos para ver todos los trabajos abiertos. Filtre por oficio, estado, ciudad, presupuesto y residencial o comercial. Cada tarjeta muestra el alcance, la ciudad y el presupuesto gratis. El nombre, teléfono, correo y dirección exacta del cliente permanecen ocultos hasta desbloquear.',
    },
    where: { en: 'Projects (top menu)', es: 'Proyectos (menú superior)' },
  },
  {
    id: 'buy',
    workflow: true,
    label: { en: 'Buy Lead or Membership', es: 'Comprar lead o membresía' },
    title: { en: 'Buy one lead or choose a membership', es: 'Compre un lead o elija una membresía' },
    narration: {
      en: `Two ways to pay. Buy a single lead for a flat ${lead} — same price for every project. Or pick a membership: Basic ${basic} leads, Pro ${pro} leads, or Premium ${premium} leads on a 12-month term. Payment is processed securely and you return to GIGGONE automatically.`,
      es: `Dos formas de pagar. Compre un lead individual por ${lead} fijo — el mismo precio para cada proyecto. O elija una membresía: Basic ${basic} leads, Pro ${pro} leads, o Premium ${premium} leads con plazo de 12 meses. El pago es seguro y regresa a GIGGONE automáticamente.`,
    },
    where: { en: 'Pricing page · "Unlock" on any project', es: 'Precios · "Desbloquear" en cualquier proyecto' },
  },
  {
    id: 'credits',
    workflow: true,
    label: { en: 'Receive Credits', es: 'Recibir créditos' },
    title: { en: 'Credits land in your account', es: 'Los créditos llegan a su cuenta' },
    narration: {
      en: 'As soon as the payment is confirmed, credits are added automatically. 1 credit equals 1 lead. A single purchase gives 1 credit; a membership adds its full monthly allowance. Your live balance shows at the top of your Dashboard and on every project page. Membership credits reset each billing cycle and do not roll over.',
      es: 'En cuanto se confirma el pago, los créditos se agregan automáticamente. 1 crédito equivale a 1 lead. Una compra individual da 1 crédito; una membresía agrega su asignación mensual completa. Su saldo aparece arriba en su Panel y en cada proyecto. Los créditos de membresía se reinician cada ciclo y no se acumulan.',
    },
    where: { en: 'Dashboard → Lead balance card', es: 'Panel → Tarjeta de saldo' },
  },
  {
    id: 'unlock',
    workflow: true,
    label: { en: 'Unlock Lead', es: 'Desbloquear lead' },
    title: { en: 'Unlock the lead', es: 'Desbloquee el lead' },
    narration: {
      en: 'Open the project you want and tap Unlock Lead. One credit is used and the contact details are released instantly. Each project is shared with at most 3 contractors, so unlocking early matters. Every lead you unlock is saved permanently in My Leads.',
      es: 'Abra el proyecto que quiere y toque Desbloquear Lead. Se usa un crédito y los datos de contacto se liberan al instante. Cada proyecto se comparte con máximo 3 contratistas, así que desbloquear temprano importa. Cada lead desbloqueado queda guardado en Mis Leads.',
    },
    where: { en: 'Project page → Unlock Lead button', es: 'Página del proyecto → Desbloquear Lead' },
  },
  {
    id: 'info',
    workflow: true,
    label: { en: 'View Homeowner Info', es: 'Ver datos del cliente' },
    title: { en: 'See the homeowner information', es: 'Vea los datos del cliente' },
    narration: {
      en: 'After unlocking, the project page shows the customer name, phone number, email, full street address and the complete project notes. You can come back any time: Dashboard, then My Leads, then open the lead. You also get a Lead Unlocked email with a link back to it.',
      es: 'Después de desbloquear, la página del proyecto muestra el nombre del cliente, teléfono, correo, dirección completa y todas las notas del proyecto. Puede volver cuando quiera: Panel, luego Mis Leads, luego abra el lead. También recibe un correo de Lead Desbloqueado con un enlace.',
    },
    where: { en: 'Dashboard → My Leads → open the lead', es: 'Panel → Mis Leads → abrir el lead' },
  },
  {
    id: 'contact',
    workflow: true,
    label: { en: 'Contact Customer', es: 'Contactar al cliente' },
    title: { en: 'Contact the customer', es: 'Contacte al cliente' },
    narration: {
      en: 'Tap the phone number to call, or use the text and email buttons right on the lead. Introduce yourself, mention the project by name, and offer a time to visit or quote. Update the lead stage — Contacted, Quoted, Won — so you can track your pipeline.',
      es: 'Toque el teléfono para llamar, o use los botones de texto y correo en el lead. Preséntese, mencione el proyecto por nombre y ofrezca un horario para visitar o cotizar. Actualice la etapa del lead — Contactado, Cotizado, Ganado — para seguir su progreso.',
    },
    where: { en: 'Unlocked lead → Call · Text · Email', es: 'Lead desbloqueado → Llamar · Texto · Correo' },
  },
  {
    id: 'win',
    workflow: false,
    label: { en: 'Win the Job', es: 'Gane el trabajo' },
    title: { en: 'Best practices for winning jobs', es: 'Buenas prácticas para ganar trabajos' },
    narration: {
      en: 'Call within the first hour — the first contractor to call usually wins. Leave a short voicemail and follow with a text. Complete your profile with trades, photos, license and insurance if you have them. Quote quickly and clearly, and always follow up two days later.',
      es: 'Llame en la primera hora — el primer contratista que llama suele ganar. Deje un mensaje breve y envíe un texto. Complete su perfil con oficios, fotos, licencia y seguro si los tiene. Cotice rápido y claro, y siempre haga seguimiento dos días después.',
    },
    where: { en: 'Profile Settings · My Leads stages', es: 'Perfil · Etapas en Mis Leads' },
  },
];

export const WORKFLOW_STEPS = TUTORIAL_STEPS.filter((s) => s.workflow);

export const BEST_PRACTICES: { title: Bi; body: Bi }[] = [
  {
    title: { en: 'Respond in the first hour', es: 'Responda en la primera hora' },
    body: {
      en: 'Speed wins. Contractors who call within 60 minutes win the job far more often.',
      es: 'La rapidez gana. Quien llama en los primeros 60 minutos gana el trabajo mucho más seguido.',
    },
  },
  {
    title: { en: 'Call, then text', es: 'Llame y luego escriba' },
    body: {
      en: 'No answer? Leave a 20-second voicemail and send a text with your name and company.',
      es: '¿No contestan? Deje un mensaje de 20 segundos y envíe un texto con su nombre y empresa.',
    },
  },
  {
    title: { en: 'Mention the project', es: 'Mencione el proyecto' },
    body: {
      en: 'Reference the exact scope and city so the homeowner knows you read their request.',
      es: 'Mencione el alcance y la ciudad exactos para que el cliente sepa que leyó su solicitud.',
    },
  },
  {
    title: { en: 'Complete your profile', es: 'Complete su perfil' },
    body: {
      en: 'Trades, service area, photos and optional license and insurance build instant trust.',
      es: 'Oficios, zona, fotos y licencia y seguro opcionales generan confianza de inmediato.',
    },
  },
  {
    title: { en: 'Quote fast and clear', es: 'Cotice rápido y claro' },
    body: {
      en: 'Send a written estimate within 24 hours of the visit with scope, price and timeline.',
      es: 'Envíe un presupuesto escrito dentro de 24 horas con alcance, precio y tiempos.',
    },
  },
  {
    title: { en: 'Follow up', es: 'Haga seguimiento' },
    body: {
      en: 'A polite follow-up two days later wins jobs others forget about. Track stages in My Leads.',
      es: 'Un seguimiento amable dos días después gana trabajos que otros olvidan. Use las etapas en Mis Leads.',
    },
  },
];

/** Quick answers to the support questions this tutorial exists to prevent. */
export const TUTORIAL_FAQ: { q: Bi; a: Bi }[] = [
  {
    q: { en: 'I paid — where are my credits?', es: 'Pagué — ¿dónde están mis créditos?' },
    a: {
      en: 'Credits are added automatically once the payment is confirmed, usually within seconds. Your balance is on the Dashboard lead balance card. Make sure you are signed in with the same email you paid with.',
      es: 'Los créditos se agregan automáticamente al confirmarse el pago, normalmente en segundos. Su saldo está en la tarjeta de saldo del Panel. Asegúrese de iniciar sesión con el mismo correo con el que pagó.',
    },
  },
  {
    q: { en: 'Where do I see the homeowner phone number?', es: '¿Dónde veo el teléfono del cliente?' },
    a: {
      en: 'On the project page right after you unlock it, and any time later under Dashboard → My Leads.',
      es: 'En la página del proyecto justo después de desbloquearlo, y después en Panel → Mis Leads.',
    },
  },
  {
    q: { en: 'Why can I not unlock a lead?', es: '¿Por qué no puedo desbloquear un lead?' },
    a: {
      en: 'The three most common reasons: your email is not verified yet, you have 0 credits left, or the project already has 3 contractors. The project page tells you which one applies.',
      es: 'Las tres razones más comunes: su correo aún no está verificado, tiene 0 créditos, o el proyecto ya tiene 3 contratistas. La página del proyecto le indica cuál aplica.',
    },
  },
  {
    q: { en: 'Do unused membership credits roll over?', es: '¿Los créditos no usados se acumulan?' },
    a: {
      en: `No. Included credits reset each billing cycle. Extra single leads are always ${lead}.`,
      es: `No. Los créditos incluidos se reinician cada ciclo. Los leads adicionales siempre cuestan ${lead}.`,
    },
  },
];

export const TUTORIAL_PATH = '/how-it-works';

/**
 * 30-SECOND VERSION — for quick homepage visitors. Four beats × ~7.5s, each
 * reusing the matching full step's animated scene, so nothing drifts.
 */
export const QUICK_STEPS: { id: TutorialStepId; caption: Bi }[] = [
  {
    id: 'browse',
    caption: {
      en: 'Create a free account and browse real projects. Scope, city and budget are free to see.',
      es: 'Cree una cuenta gratis y explore proyectos reales. Alcance, ciudad y presupuesto se ven gratis.',
    },
  },
  {
    id: 'credits',
    caption: {
      en: `Buy one lead for ${lead} or pick a membership. Credits appear on your Dashboard instantly — 1 credit = 1 lead.`,
      es: `Compre un lead por ${lead} o elija una membresía. Los créditos aparecen en su Panel al instante — 1 crédito = 1 lead.`,
    },
  },
  {
    id: 'unlock',
    caption: {
      en: 'Tap Unlock Lead. The homeowner name, phone, email and address appear right away — max 3 contractors per project.',
      es: 'Toque Desbloquear Lead. El nombre, teléfono, correo y dirección del cliente aparecen al instante — máx. 3 contratistas.',
    },
  },
  {
    id: 'contact',
    caption: {
      en: 'Call or text within the first hour. Your leads stay saved in Dashboard → My Leads.',
      es: 'Llame o escriba en la primera hora. Sus leads quedan guardados en Panel → Mis Leads.',
    },
  },
];

/** End-of-tutorial calls to action (paths are localized by useI18n().path). */
export const TUTORIAL_CTAS: { key: string; path: string; label: Bi; primary?: boolean }[] = [
  { key: 'register', path: '/register', label: { en: 'Create Account', es: 'Crear cuenta' }, primary: true },
  { key: 'browse', path: '/jobs', label: { en: 'Browse Leads', es: 'Ver leads' } },
  { key: 'pricing', path: '/pricing', label: { en: 'View Pricing', es: 'Ver precios' } },
  { key: 'membership', path: '/memberships', label: { en: 'Join Membership', es: 'Unirse a membresía' } },
];

export const bi = (v: Bi, lang: Lang) => (lang === 'es' ? v.es : v.en);

export const stepNarration = (s: TutorialStep, audience: TutorialAudience, lang: Lang) =>
  bi(audience === 'gc' && s.narrationGc ? s.narrationGc : s.narration, lang);
